import { resolveThemeDefaults } from '@mfw/common';
import { TranslationsProvider, Iconify } from '@mfw/components';
import Typography from '@mui/material/Typography';
import { useRouteError } from 'react-router-dom';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import * as Sentry from '@sentry/react';
import Box from '@mui/material/Box';
import { useIntl } from 'react-intl';
import React, { useLayoutEffect, useEffect } from 'react';

import { ThemeProvider } from '../theme';
import messages from './messages';
import { onSurface } from '../theme/onSurface';

/**
 * AppThemeProvider applies tenant/org colors imperatively to
 * document.documentElement, so they survive the crash of the React tree and
 * this page inherits the tenant's branding for free. On a cold load (the app
 * crashed before AppThemeProvider ever ran) the vars are missing — backfill
 * them with the catalog defaults so the page still renders fully themed.
 */
function ensureThemeVars() {
  const root = document.documentElement;
  const defaults = resolveThemeDefaults();
  for (const [name, value] of Object.entries(defaults)) {
    if (!root.style.getPropertyValue(name)) {
      root.style.setProperty(name, value);
    }
  }
}

function RouteErrorView() {
  const { formatMessage } = useIntl();

  useLayoutEffect(() => {
    ensureThemeVars();
  }, []);

  return (
    <Stack
      bgcolor="background.default"
      justifyContent="center"
      color="text.primary"
      alignItems="center"
      textAlign="center"
      minHeight="100vh"
      p={3}
    >
      <Stack alignItems="center" maxWidth={420} gap={2}>
        <Box
          bgcolor="background.paper"
          justifyContent="center"
          color={onSurface.primary}
          alignItems="center"
          borderRadius="50%"
          display="flex"
          height={96}
          width={96}
          mb={1}
        >
          <Iconify
            icon="solar:danger-triangle-bold-duotone"
            size="large"
            width={48}
          />
        </Box>
        <Typography variant="h4">
          {formatMessage(messages.routeErrorTitle)}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {formatMessage(messages.routeErrorDescription)}
        </Typography>
        <Stack direction="row" gap={2} mt={2}>
          <Button
            onClick={() => window.location.reload()}
            data-testid="route-error-refresh"
            variant="contained"
            size="large"
          >
            {formatMessage(messages.routeErrorRefresh)}
          </Button>
          <Button
            data-testid="route-error-back-home"
            variant="outlined"
            color="inherit"
            size="large"
            href="/"
          >
            {formatMessage(messages.backToHome)}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

/**
 * Absolute last resort: shown only if the themed view itself crashes (e.g.
 * the theme or intl stack is what broke). Inline styles only, no providers.
 * Reuses the defaultMessage strings so the copy stays in one place.
 */
function BareErrorView() {
  return (
    <div
      style={{
        alignItems: 'center',
        background: '#161C24',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'sans-serif',
        gap: 16,
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: 22, margin: 0 }}>
        {messages.routeErrorTitle.defaultMessage}
      </h1>
      <p style={{ color: '#919EAB', margin: 0 }}>
        {messages.routeErrorDescription.defaultMessage}
      </p>
      <button
        onClick={() => window.location.reload()}
        data-testid="route-error-refresh"
        style={{
          background: '#FFFFFF',
          border: 0,
          borderRadius: 8,
          color: '#161C24',
          cursor: 'pointer',
          fontSize: 15,
          padding: '10px 22px',
        }}
        type="button"
      >
        {messages.routeErrorRefresh.defaultMessage}
      </button>
    </div>
  );
}

type SafetyBoundaryProps = { children: React.ReactNode };
type SafetyBoundaryState = { hasError: boolean };

class SafetyBoundary extends React.Component<
  SafetyBoundaryProps,
  SafetyBoundaryState
> {
  state: SafetyBoundaryState = { hasError: false };

  static getDerivedStateFromError(): SafetyBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) return <BareErrorView />;
    return this.props.children;
  }
}

/**
 * Root errorElement. Renders OUTSIDE AppProviders (an errorElement replaces
 * the route's element entirely), so it brings its own TranslationsProvider +
 * ThemeProvider; tenant colors come from the CSS vars already on :root (see
 * ensureThemeVars).
 */
export default function RouteErrorPage() {
  const error = useRouteError();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <SafetyBoundary>
      <TranslationsProvider>
        <ThemeProvider>
          <RouteErrorView />
        </ThemeProvider>
      </TranslationsProvider>
    </SafetyBoundary>
  );
}
